import { useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, CheckCircle2, Loader2, Check } from "lucide-react";
import { formatEuro } from "../../lib/savingsFormula";
import { RDV_URL } from "../../lib/api";

const EmailEstimate = ({ onSend }) => {
    const [email, setEmail] = useState("");
    const [optin, setOptin] = useState(false);
    const [state, setState] = useState("idle"); // idle | sending | sent
    const [error, setError] = useState("");
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);

    const submit = async (e) => {
        e.preventDefault();
        if (!valid) return setError("Saisissez une adresse e-mail valide.");
        if (!optin) return setError("Cochez la case pour recevoir votre estimation par e-mail.");
        setError("");
        setState("sending");
        try {
            await onSend(email);
            setState("sent");
        } catch {
            setState("idle");
            setError("L'envoi n'a pas abouti. Merci de réessayer dans quelques minutes.");
        }
    };

    if (state === "sent")
        return (
            <div role="status" data-testid="sim-email-sent" className="flex items-start gap-3 bg-sand rounded-[10px] px-4 py-3.5 text-[15px] leading-normal text-navy animate-fade-up">
                <CheckCircle2 size={20} className="text-gold-text shrink-0 mt-0.5" />
                <span>C'est envoyé. Vous recevez votre estimation dans quelques minutes ; pensez à vérifier vos indésirables.</span>
            </div>
        );

    return (
        <form onSubmit={submit} noValidate className="flex flex-col gap-3" data-testid="sim-email-form">
            <label htmlFor="sim-mail" className="text-sm font-medium text-grey-text">Ou recevez votre estimation par e-mail</label>
            <div className="flex flex-col sm:flex-row gap-3">
                <input
                    id="sim-mail"
                    type="email"
                    autoComplete="email"
                    placeholder="prenom@societe.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="sim-email-input"
                    className="w-full sm:flex-1 min-w-0 h-[50px] border-[1.5px] border-border-strong rounded-[10px] px-3.5 text-[15px] text-navy bg-white outline-none focus:border-navy transition-colors"
                />
                <button
                    type="submit"
                    data-testid="sim-email-submit"
                    className={`h-[50px] px-5 rounded-[10px] text-[15px] font-semibold inline-flex items-center justify-center gap-2 transition-colors sm:min-w-[200px] ${
                        valid && optin ? "bg-navy text-white hover:bg-navy-band" : "bg-sand text-grey-text-soft border border-border-default"
                    }`}
                >
                    {state === "sending" && <Loader2 size={16} className="animate-spin" />}
                    Recevoir par e-mail
                </button>
            </div>
            <div className="flex gap-3 items-start">
                <button
                    type="button"
                    role="checkbox"
                    aria-checked={optin}
                    aria-labelledby="sim-optin-txt"
                    onClick={() => setOptin((v) => !v)}
                    data-testid="sim-email-optin"
                    className={`w-5 h-5 mt-0.5 rounded-[5px] shrink-0 flex items-center justify-center ${optin ? "bg-navy text-white" : "border-[1.5px] border-border-stronger bg-white"}`}
                >
                    {optin && <Check size={14} strokeWidth={3} />}
                </button>
                <span id="sim-optin-txt" className="text-[13px] leading-relaxed text-grey-text">
                    J'accepte de recevoir mon estimation par e-mail et d'être recontacté par FOD by DMB Partners au sujet de mes frais bancaires.{" "}
                    <Link to="/politique-de-confidentialite" className="text-navy underline underline-offset-2">Politique de confidentialité</Link>
                </span>
            </div>
            {error && <span role="alert" data-testid="sim-email-error" className="text-sm text-error">{error}</span>}
        </form>
    );
};

const Amount = ({ label, value, high, testid }) => (
    <div className="flex flex-col items-center md:items-start gap-1">
        <span className={`text-[11px] md:text-xs font-semibold uppercase tracking-[0.12em] ${high ? "text-gold-text" : "text-grey-text"}`}>{label}</span>
        <span data-testid={testid} className={`font-bold text-[34px] sm:text-[44px] md:text-[52px] leading-none tracking-[-0.03em] tabular-nums ${high ? "text-gold-text" : "text-navy"}`}>
            {formatEuro(value)}
        </span>
    </div>
);

export const ResultPanel = ({ estimate, chips, onRdv, onSendEmail, onRestart }) => (
    <div className="flex flex-col gap-5 animate-fade-up" data-testid="sim-result">
        <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-gold-text">Votre estimation</span>
            <h2 className="m-0 text-[22px] md:text-[30px] font-bold leading-[1.25] tracking-[-0.015em] text-navy">Voici ce que votre entreprise pourrait récupérer.</h2>
        </div>
        <div className="bg-cream rounded-xl px-5 md:px-7 py-6 flex flex-col gap-3 text-center md:text-left">
            <span className="text-sm md:text-[15px] text-grey-text">Économies estimées sur vos frais bancaires, par an</span>
            <div className="flex flex-col items-center gap-2 md:flex-row md:flex-wrap md:items-end md:gap-x-6 md:gap-y-3">
                <div className="flex flex-col items-center md:items-start gap-3">
                    <Amount label="Estimation moyenne" value={estimate.average} testid="sim-amount-average" />
                    <span className="hidden md:block w-[140px] h-1.5 bg-gold" />
                </div>
                <span aria-hidden="true" className="md:hidden text-2xl leading-none text-border-stronger">↓</span>
                <span aria-hidden="true" className="hidden md:block text-2xl text-border-stronger pb-8">→</span>
                <div className="md:pb-[18px]"><Amount high label="Estimation haute" value={estimate.high} testid="sim-amount-high" /></div>
                <span className="md:hidden w-[140px] h-1.5 bg-gold mt-2" data-testid="sim-gold-bar-mobile" />
            </div>
            {estimate.factoring && (
                <p className="m-0 text-[13px] text-grey-text-soft" data-testid="sim-factoring-note">Affacturage : non inclus dans cette fourchette, chiffré à partir de votre contrat.</p>
            )}
        </div>
        <div className="flex flex-wrap justify-center md:justify-start gap-2" data-testid="sim-chips">
            {chips.map((c) => <span key={c} className="bg-sand rounded-full px-3.5 py-1.5 text-[13px] font-medium text-navy">{c}</span>)}
        </div>
        <div className="bg-navy text-cream rounded-xl p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 shadow-[0_18px_40px_-20px_rgba(15,27,45,0.6)]">
            <div className="flex-1 flex flex-col gap-2">
                <span className="text-lg md:text-[22px] font-bold leading-snug">Faites confirmer ce chiffre par un ancien banquier.</span>
                <span className="text-sm md:text-[15px] leading-relaxed text-on-navy-soft">30 minutes, gratuit et sans engagement. Nous regardons vos relevés avec vous et vous donnons le montant réel.</span>
            </div>
            <a
                href={RDV_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onRdv}
                data-testid="sim-rdv-cta"
                className="md:flex-1 h-[60px] px-6 rounded-lg bg-gold text-navy text-[17px] font-bold inline-flex items-center justify-center gap-2.5 transition-[transform,box-shadow] hover:-translate-y-[1px] hover:shadow-lg"
            >
                <Calendar size={20} /> Prendre rendez-vous
            </a>
        </div>
        <EmailEstimate onSend={onSendEmail} />
        <div className="border-t border-border-soft pt-4 text-center">
            <button type="button" onClick={onRestart} data-testid="sim-restart" className="text-[15px] text-grey-text underline underline-offset-4 hover:text-navy transition-colors">Refaire la simulation</button>
        </div>
        <p className="m-0 text-xs text-grey-text-soft">Estimation indicative. Le montant réel est établi après audit de vos relevés des 12 derniers mois.</p>
    </div>
);
